import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  type Connection,
  type Edge,
  type Node,
  type ReactFlowInstance,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { nodeTypes } from './nodes';
import { NodePropertiesPanel } from './panels';
import { NodeToolbar } from './toolbar';
import { useWorkflowStore } from '../../stores';
import type {
  WorkflowNode,
  WorkflowEdge,
  NodeType,
} from '../../types/workflow';

// 导入节点样式
import './nodes/nodeStyles.css';

// Node data interface for FlowCanvas
interface NodeData extends Record<string, unknown> {
  label: string;
  description?: string;
  type: NodeType;
}

export default function FlowCanvas() {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  
  // 从store获取状态
  const {
    currentWorkflow,
    selectedNode,
    setSelectedNode,
    addNode,
    updateNode,
    addEdge: addWorkflowEdge,
    removeEdge,
    undo,
    redo,
    canUndo,
    canRedo,
    saveToHistory,
  } = useWorkflowStore();

  // 本地节点和边状态
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdge>([]);

  // 同步store中的工作流到本地状态
  useEffect(() => {
    if (currentWorkflow) {
      setNodes(currentWorkflow.nodes as Node[]);
      setEdges(currentWorkflow.edges);
    }
  }, [currentWorkflow, currentWorkflow?.nodes, currentWorkflow?.edges, setNodes, setEdges]);

  // 连接节点
  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge: WorkflowEdge = {
        ...params,
        id: `e-${params.source}-${params.target}-${Date.now()}`,
      };
      setEdges((eds) => addEdge(newEdge, eds));
      addWorkflowEdge(newEdge);
    },
    [addWorkflowEdge, setEdges]
  );

  // 节点选择
  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      setSelectedNode(node as unknown as WorkflowNode);
    },
    [setSelectedNode]
  );

  // 画布点击
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, [setSelectedNode]);

  // 边删除
  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      deletedEdges.forEach((edge) => {
        removeEdge(edge.id);
      });
    },
    [removeEdge]
  );

  // 节点数据更新
  const handleNodeDataChange = useCallback(
    (nodeId: string, data: Partial<NodeData>) => {
      updateNode(nodeId, data as Record<string, unknown>);
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
        )
      );
    },
    [updateNode, setNodes]
  );

  // 拖拽开始
  const onDragStart = useCallback((event: React.DragEvent, nodeType: NodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  // 拖拽经过画布
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // 放置节点
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (!reactFlowInstance || !reactFlowWrapper.current) {
        console.warn('ReactFlow instance or wrapper not ready');
        return;
      }

      // 尝试获取拖拽数据（支持多种数据类型）
      const nodeType = 
        event.dataTransfer.getData('application/reactflow') ||
        event.dataTransfer.getData('text/plain');
      
      if (!nodeType) {
        console.warn('No node type found in drop data');
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      console.log('Adding node:', nodeType, 'at position:', position);
      addNode(nodeType as NodeType, position);
    },
    [reactFlowInstance, addNode]
  );

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          if (canRedo()) redo();
        } else {
          if (canUndo()) undo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, canUndo, canRedo]);

  // 保存历史记录当节点或边变化时
  useEffect(() => {
    if (currentWorkflow && (nodes.length > 0 || edges.length > 0)) {
      // 防抖保存历史
      const timeoutId = setTimeout(() => {
        saveToHistory();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.length, edges.length]);

  // 如果没有当前工作流，显示提示
  if (!currentWorkflow) {
    return (
      <div
        style={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f9fafb',
          color: '#6b7280',
        }}
      >
        <span style={{ fontSize: '64px', marginBottom: '16px' }}>🔄</span>
        <h2 style={{ margin: '0 0 8px 0', color: '#374151' }}>没有选中的工作流</h2>
        <p style={{ margin: 0 }}>请先创建或选择一个工作流</p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* 左侧工具栏 */}
      <NodeToolbar onDragStart={onDragStart} />

      {/* 中间画布区域 */}
      <div
        ref={reactFlowWrapper}
        style={{
          flex: 1,
          height: '100%',
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onEdgesDelete={onEdgesDelete}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onInit={setReactFlowInstance}
          nodeTypes={nodeTypes}
          fitView={false}
          attributionPosition="bottom-left"
          deleteKeyCode={['Backspace', 'Delete']}
          multiSelectionKeyCode={['Control', 'Meta']}
          selectionKeyCode={['Shift']}
        >
          <Background color="#d1d5db" gap={20} size={1} />
          <Controls />
          <MiniMap
            nodeStrokeWidth={3}
            zoomable
            pannable
            style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
            }}
          />
          
          {/* 顶部工具栏 */}
          <Panel position="top-center">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <span style={{ fontWeight: 600, color: '#374151' }}>
                {currentWorkflow.name}
              </span>
              <span style={{ color: '#9ca3af' }}>|</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                {nodes.length} 节点 · {edges.length} 连接
              </span>
            </div>
          </Panel>

          {/* 撤销/重做按钮 */}
          <Panel position="top-left">
            <div
              style={{
                display: 'flex',
                gap: '4px',
                background: 'white',
                padding: '4px',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <button
                onClick={undo}
                disabled={!canUndo()}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  background: canUndo() ? '#f3f4f6' : '#f9fafb',
                  color: canUndo() ? '#374151' : '#9ca3af',
                  cursor: canUndo() ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
                title="撤销 (Ctrl+Z)"
              >
                ↩️ 撤销
              </button>
              <button
                onClick={redo}
                disabled={!canRedo()}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  background: canRedo() ? '#f3f4f6' : '#f9fafb',
                  color: canRedo() ? '#374151' : '#9ca3af',
                  cursor: canRedo() ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
                title="重做 (Ctrl+Shift+Z)"
              >
                ↪️ 重做
              </button>
            </div>
          </Panel>
        </ReactFlow>
      </div>

      {/* 右侧属性面板 */}
      <NodePropertiesPanel
        selectedNode={selectedNode as unknown as Node<NodeData>}
        onNodeDataChange={handleNodeDataChange}
      />
    </div>
  );
}
